/**
 * [INPUT]: 浏览器 File、游戏 ID、AdminApi 的 presign/confirm 端口。
 * [OUTPUT]: 封面版位规格、比例校验、SHA-256 计算与已确认 GameAsset。
 * [POS]: admin-web 游戏媒体基础设施；页面只编排槽位状态，不直接依赖对象存储协议。
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import type { AdminApi } from './admin-api';
import type { GameAsset, GameCoverSlot } from '../types';

export const COVER_SPECS: Record<GameCoverSlot, { label: string; ratio: number; ratioText: '4:3' | '4:5' | '1:1'; usage: string }> = {
  hero: { label: '主推封面', ratio: 4 / 3, ratioText: '4:3', usage: '首页主推横幅' },
  card: { label: '卡片封面', ratio: 4 / 5, ratioText: '4:5', usage: '双列世界卡片' },
  thumb: { label: '缩略图', ratio: 1, ratioText: '1:1', usage: '目录与列表缩略图' },
};

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const RATIO_TOLERANCE = 0.02;

export type CoverValidation = { width: number; height: number; ratio: number };

export async function validateCoverFile(file: File, slot: GameCoverSlot): Promise<CoverValidation> {
  if (!file.type.startsWith('image/')) throw new Error('封面必须是图片文件。');
  if (file.size <= 0 || file.size > MAX_FILE_BYTES) throw new Error('图片大小必须大于 0 且不超过 50 MiB。');
  const { width, height } = await readImageSize(file);
  return validateCoverDimensions(width, height, slot);
}

export function validateCoverDimensions(width: number, height: number, slot: GameCoverSlot): CoverValidation {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error('无法读取图片尺寸，请换一张图片。');
  }
  const ratio = width / height;
  const expected = COVER_SPECS[slot].ratio;
  const relativeError = Math.abs(ratio - expected) / expected;
  if (relativeError - RATIO_TOLERANCE > Number.EPSILON * 8) {
    throw new Error(`${COVER_SPECS[slot].label}需要接近 ${COVER_SPECS[slot].ratioText}，当前是 ${formatRatio(ratio)}。`);
  }
  return { width, height, ratio };
}

export async function uploadCoverAsset(api: AdminApi, gameId: string, file: File): Promise<GameAsset> {
  if (api.uploadAsset) return api.uploadAsset(gameId, file);
  const checksum = await sha256Hex(file);
  const presigned = await api.presignAssetUpload({ gameId, filename: file.name, contentType: file.type, size: file.size, checksum });
  if (!/^https?:\/\//i.test(presigned.uploadUrl)) throw new Error('服务端没有返回有效的对象存储上传地址。');
  const response = await fetch(presigned.uploadUrl, { method: 'PUT', headers: presigned.headers, body: file });
  if (!response.ok) throw new Error(`图片上传失败（${response.status}）。请稍后重试。`);
  return api.confirmAssetUpload({
    gameId,
    key: presigned.key,
    contentType: file.type,
    size: file.size,
    checksum: presigned.checksum || checksum,
  });
}

export function formatRatio(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '未知比例';
  return `${value.toFixed(2)}:1`;
}

function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      if (image.naturalWidth <= 0 || image.naturalHeight <= 0) reject(new Error('无法读取图片尺寸，请换一张图片。'));
      else resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('图片无法读取，请确认文件没有损坏。'));
    };
    image.src = url;
  });
}

async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
